import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Mail, FileText, BarChart3, Presentation, Video, Cloud, Calendar, CheckCircle, Star, Home, Search, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const GoogleWorkspacePresentationPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Données des slides
  const slides = [
    // Slide 1 - Présentation générale
    {
      id: 'intro',
      title: 'Bienvenue dans Google Workspace',
      subtitle: 'L\'écosystème collaboratif de Google',
      content: {
        description: 'Google Workspace révolutionne la collaboration avec des outils natifs cloud, une intégration IA avancée et une simplicité d\'usage qui booste la productivité d\'équipe.',
        features: [
          'Collaboration temps réel native',
          'IA intégrée dans chaque outil',
          'Simplicité et intuitivité',
          'Accès universel cloud',
          'Sécurité Google enterprise',
          'Intégration écosystème complète'
        ]
      },
      icon: Search,
      color: 'from-green-600 via-blue-600 to-purple-600',
      bgColor: 'from-green-50 to-blue-50'
    },
    // Slide 2 - Gmail
    {
      id: 'gmail',
      title: 'Gmail',
      subtitle: 'Email intelligent avec IA intégrée',
      content: {
        description: 'Gmail Business offre bien plus qu\'une messagerie : IA pour tri automatique, recherche intelligente, intégration Meet et collaboration seamless avec tous les outils Google.',
        features: [
          'IA pour organisation automatique',
          'Recherche ultra-puissante',
          'Intégration Meet natuve',
          'Labels et filtres intelligents',
          'Sécurité anti-spam avancée',
          'Mode confidentiel et expiration'
        ]
      },
      icon: Mail,
      color: 'from-red-500 to-red-600',
      bgColor: 'from-red-50 to-red-100'
    },
    // Slide 3 - Google Docs
    {
      id: 'docs',
      title: 'Google Docs',
      subtitle: 'Rédaction collaborative en temps réel',
      content: {
        description: 'Google Docs transforme la création de documents avec la co-édition simultanée, les suggestions IA et une intégration parfaite avec l\'écosystème Google.',
        features: [
          'Co-édition simultanée temps réel',
          'Suggestions IA et correction',
          'Commentaires et révisions streamlined',
          'Historique complet des versions',
          'Partage et permissions granulaires',
          'Mode hors ligne automatique'
        ]
      },
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'from-blue-50 to-blue-100'
    },
    // Slide 4 - Google Sheets
    {
      id: 'sheets',
      title: 'Google Sheets',
      subtitle: 'Tableurs collaboratifs avec IA',
      content: {
        description: 'Google Sheets combine puissance d\'analyse, collaboration temps réel et intelligence artificielle pour transformer vos données en insights actionnables.',
        features: [
          'Formules et fonctions avancées',
          'Collaboration multi-utilisateurs',
          'Graphiques dynamiques intelligents',
          'Automatisation avec Apps Script',
          'Import de données multi-sources',
          'Analyse prédictive IA'
        ]
      },
      icon: BarChart3,
      color: 'from-green-500 to-green-600',
      bgColor: 'from-green-50 to-green-100'
    },
    // Slide 5 - Google Slides
    {
      id: 'slides',
      title: 'Google Slides',
      subtitle: 'Présentations collaboratives dynamiques',
      content: {
        description: 'Créez des présentations impactantes avec la collaboration temps réel, des templates intelligents et une intégration multimedia native.',
        features: [
          'Templates professionnels IA',
          'Collaboration temps réel',
          'Animations et transitions fluides',
          'Intégration YouTube et images',
          'Présentation en ligne interactive',
          'Export et partage universel'
        ]
      },
      icon: Presentation,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'from-orange-50 to-orange-100'
    },
    // Slide 6 - Google Meet
    {
      id: 'meet',
      title: 'Google Meet',
      subtitle: 'Visioconférences professionnelles HD',
      content: {
        description: 'Google Meet offre des visioconférences enterprise avec qualité HD, fonctionnalités IA et intégration native avec Calendar et Gmail.',
        features: [
          'Qualité vidéo HD adaptative',
          'Suppression bruit IA',
          'Enregistrement et transcription',
          'Tableaux blancs collaboratifs',
          'Intégration Calendar seamless',
          'Sécurité enterprise Google'
        ]
      },
      icon: Video,
      color: 'from-teal-500 to-teal-600',
      bgColor: 'from-teal-50 to-teal-100'
    },
    // Slide 7 - Google Drive
    {
      id: 'drive',
      title: 'Google Drive',
      subtitle: 'Stockage cloud intelligent et universel',
      content: {
        description: 'Google Drive centralise vos fichiers avec partage intelligent, recherche IA et synchronisation universelle sur tous vos appareils.',
        features: [
          'Stockage illimité (Business)',
          'Recherche IA dans contenus',
          'Partage et permissions avancées',
          'Synchronisation multi-appareils',
          'Versioning automatique',
          'Intégration native tous outils'
        ]
      },
      icon: Cloud,
      color: 'from-yellow-500 to-yellow-600',
      bgColor: 'from-yellow-50 to-yellow-100'
    },
    // Slide 8 - Google Calendar
    {
      id: 'calendar',
      title: 'Google Calendar',
      subtitle: 'Agenda intelligent et planification IA',
      content: {
        description: 'Google Calendar organise votre temps avec IA prédictive, intégration Meet native et synchronisation parfaite avec tous vos outils Google.',
        features: [
          'Planification intelligente IA',
          'Intégration Meet automatique',
          'Calendriers partagés d\'équipe',
          'Suggestions créneaux optimaux',
          'Synchronisation universelle',
          'Rappels et goals personnalisés'
        ]
      },
      icon: Calendar,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100'
    },
    // Slide 9 - Conclusion et retour
    {
      id: 'conclusion',
      title: 'Prêt à optimiser votre collaboration ?',
      subtitle: 'Explorez nos guides Google Workspace',
      content: {
        description: 'Maintenant que vous connaissez l\'écosystème Google Workspace, découvrez nos guides pratiques pour maîtriser chaque outil et révolutionner votre collaboration.',
        features: [
          'Guides collaboration experts',
          'Configuration optimale cloud',
          'Intégrations IA avancées',
          'Workflows d\'équipe efficaces',
          'Sécurité enterprise',
          'Productivité décuplée'
        ]
      },
      icon: Star,
      color: 'from-green-600 to-purple-600',
      bgColor: 'from-green-50 to-purple-100',
      isConclusion: true
    }
  ];

  const currentSlideData = slides[currentSlide];
  const IconComponent = currentSlideData.icon;

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { id: 'google-workspace', label: 'Google Workspace' },
              { id: 'presentation', label: 'Présentation des outils' }
            ]}
          />
        </div>
      </div>

      {/* Hero Banner */}
      <section className={`relative overflow-hidden bg-gradient-to-r ${currentSlideData.color}`}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-6 sm:mb-8">
              <IconComponent className="h-10 w-10 sm:h-12 sm:w-12 lg:h-16 lg:w-16 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight">
              {currentSlideData.title}
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-white text-opacity-90 max-w-3xl mx-auto px-4">
              {currentSlideData.subtitle}
            </p>
          </div>
        </div>
        
        {/* Floating elements - hidden on mobile */}
        <div className="hidden sm:block absolute top-20 left-10 w-16 h-16 lg:w-20 lg:h-20 bg-white bg-opacity-10 rounded-full animate-bounce"></div>
        <div className="hidden sm:block absolute bottom-20 right-10 w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-full animate-pulse"></div>
        <div className="hidden lg:block absolute top-1/2 left-1/4 w-10 h-10 lg:w-12 lg:h-12 bg-white bg-opacity-10 rounded-full animate-ping"></div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Slide Content */}
        <section className={`bg-gradient-to-br ${currentSlideData.bgColor} rounded-3xl p-6 sm:p-8 lg:p-12 mb-12 shadow-xl`}>
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 leading-relaxed mb-6 sm:mb-8">
              {currentSlideData.content.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {currentSlideData.content.features.map((feature, index) => (
              <div key={index} className="flex items-center bg-white bg-opacity-60 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-3 sm:mr-4 flex-shrink-0" />
                <span className="text-base sm:text-lg font-semibold text-gray-800">{feature}</span>
              </div>
            ))}
          </div>

          {/* Call to Action pour slide de conclusion */}
          {currentSlideData.isConclusion && (
            <div className="text-center mt-8 sm:mt-12">
              <Link to="/google-workspace">
                <Button className="bg-gradient-to-r from-green-600 to-purple-600 hover:from-green-700 hover:to-purple-700 text-white font-bold px-6 sm:px-8 lg:px-12 py-4 sm:py-6 rounded-full text-lg sm:text-xl shadow-2xl transform hover:scale-105 transition-all duration-300">
                  <Star className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-4" />
                  Accéder aux guides détaillés
                  <ArrowRight className="ml-2 sm:ml-4 h-6 w-6 sm:h-8 sm:w-8" />
                </Button>
              </Link>
            </div>
          )}
        </section>

        {/* Navigation Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6 sm:mb-8 gap-4">
          <Button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`${
              currentSlide === 0 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:scale-105'
            } bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 text-sm sm:text-base w-full sm:w-auto`}
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Précédent
          </Button>

          <div className="flex items-center space-x-2 order-first sm:order-none">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-green-600 scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`${
              currentSlide === slides.length - 1 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:scale-105'
            } bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 text-sm sm:text-base w-full sm:w-auto`}
          >
            Suivant
            <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
          </Button>
        </div>

        {/* Slide Counter */}
        <div className="text-center mb-6 sm:mb-0">
          <div className="inline-flex items-center bg-white rounded-full px-4 sm:px-6 py-2 sm:py-3 shadow-lg">
            <span className="text-base sm:text-lg font-semibold text-gray-700">
              Slide {currentSlide + 1} sur {slides.length}
            </span>
          </div>
        </div>

        {/* Back to Google Workspace */}
        <div className="text-center mt-8 sm:mt-12">
          <Link to="/google-workspace">
            <Button className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg shadow-lg transform hover:scale-105 transition-all duration-300 w-full sm:w-auto">
              <Home className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3" />
              Retour à Google Workspace
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default GoogleWorkspacePresentationPage;